import { cellAt, threatsAgainst, type GameContext } from '@archon3d/core';
import type { SessionSnapshot } from './session';

/** The lessons of a first game, in the order a game tends to raise them. */
export type GuideStep =
  | 'welcome'
  | 'targets'
  | 'attack'
  | 'aftermath'
  | 'cycle'
  | 'power'
  | 'spells'
  | 'threats'
  | 'done';

export interface GuideHint {
  step: GuideStep;
  title: string;
  text: string;
}

export const GUIDE_STEPS: readonly GuideStep[] = [
  'welcome',
  'targets',
  'attack',
  'aftermath',
  'cycle',
  'power',
  'spells',
  'threats',
  'done',
];

/** Short enough for one line on the sign beyond the table, so nothing has to scroll. */
const HINTS: Record<GuideStep, { title: string; text: string }> = {
  welcome: {
    title: 'Welcome to RPG-Arena',
    text: 'Tap one of your pieces to see where it can go. Yours stand nearest you.',
  },
  targets: {
    title: 'Where it can go',
    text: 'Lit squares are moves; a ring on an enemy is an attack, green good odds, red poor.',
  },
  attack: {
    title: 'A fight decides a capture',
    text: 'Captures use your chosen mode: tabletop Arena combat, Card support, Roll, or Watch AI.',
  },
  aftermath: {
    title: 'After the fight',
    text: 'Won: your piece takes the square. Lost: it is gone. Wounds heal a little each turn.',
  },
  cycle: {
    title: 'The cycle turned',
    text: 'Cycling squares follow the luminosity: Light is strong when bright, Dark when dark.',
  },
  power: {
    title: 'A power point',
    text: 'Hold every power point to win; a piece standing on one is safe from spells.',
  },
  spells: {
    title: 'Your mage',
    text: 'Spells spend magic points. Recharge on a power point to cast again; the action tablet shows costs.',
  },
  threats: {
    title: 'A warning',
    text: 'An amber ring under your piece: the enemy could take it. The odds show your chance.',
  },
  done: {
    title: 'That is the basics',
    text: 'The hints stop here. Settings brings them back for another game.',
  },
};

/**
 * How a capture is settled, told for the game in hand rather than as a list of every mode
 * (client-session:quality#13, x-player-journey#7).
 */
const ATTACK_BY_MODE: Record<NonNullable<GameContext['combatMode']>, string> = {
  arena: 'You fight it out in the arena: steer your piece, strike, and dodge.',
  card: 'Each side picks a support card in secret; then the odds decide it.',
  roll: 'The odds decide it: the stronger piece is likelier to win the roll.',
  watch: 'The AI fights it out in the arena for both sides while you watch.',
};

export function guideHint(step: GuideStep, mode?: GameContext['combatMode']): GuideHint {
  const hint = HINTS[step];
  return step === 'attack' && mode
    ? { step, title: hint.title, text: ATTACK_BY_MODE[mode] }
    : { step, ...hint };
}

export interface GuideStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const GUIDE_KEY = 'archon3d.guide';

/** The steps a player has been through, from an earlier visit. */
export function loadGuideSeen(storage: GuideStorage | null): GuideStep[] {
  try {
    const raw = JSON.parse(storage?.getItem(GUIDE_KEY) ?? 'null') as { seen?: unknown } | null;
    const seen = Array.isArray(raw?.seen) ? raw.seen : [];
    return GUIDE_STEPS.filter((s) => seen.includes(s));
  } catch {
    return [];
  }
}

export function saveGuideSeen(storage: GuideStorage | null, seen: readonly GuideStep[]): void {
  try {
    storage?.setItem(GUIDE_KEY, JSON.stringify({ seen }));
  } catch {
    // The lessons repeat next visit; nothing worse.
  }
}

/** Whether the person at this client is to move and free to act. */
function humanTurn(s: SessionSnapshot): boolean {
  return s.state.phase === 'play' && s.seats[s.state.activeFaction] === 'human' && !s.busy;
}

/**
 * The guided first game: one lesson at a time, each at the moment the board first raises it,
 * each shown once and remembered across visits. A lesson stays up until the player taps Got it
 * or the next lesson takes its place; after the last one the guide falls silent for good.
 */
export class Guide {
  private readonly seen: Set<GuideStep>;
  private current: GuideStep | null = null;
  private lastPhase: number | null = null;
  private lastCtx: GameContext | null = null;

  constructor(seen: readonly GuideStep[] = []) {
    this.seen = new Set(seen);
  }

  get seenSteps(): GuideStep[] {
    return GUIDE_STEPS.filter((s) => this.seen.has(s));
  }

  /** The lesson to show for this snapshot, or none. */
  observe(snapshot: SessionSnapshot): GuideHint | null {
    // A new game starts its cycle afresh: the phase the last one stopped at is no turn
    // (client-session:bugs#7).
    if (snapshot.ctx !== this.lastCtx) this.lastPhase = null;
    this.lastCtx = snapshot.ctx;
    const phase = snapshot.state.luminosity.phase;
    const turned = this.lastPhase !== null && this.lastPhase !== phase;
    this.lastPhase = phase;
    if (this.seen.has('done')) return null;
    const next = this.trigger(snapshot, turned);
    if (next && next !== this.current) {
      if (this.current) this.seen.add(this.current);
      this.current = next;
    }
    return this.current ? guideHint(this.current, snapshot.ctx.combatMode) : null;
  }

  /** The player has read the lesson on show. */
  dismiss(): void {
    if (!this.current) return;
    this.seen.add(this.current);
    this.current = null;
  }

  /** Forget everything, for another first game. */
  reset(): void {
    this.seen.clear();
    this.current = null;
    this.lastPhase = null;
    this.lastCtx = null;
  }

  private trigger(s: SessionSnapshot, turned: boolean): GuideStep | null {
    const { ctx, state } = s;
    const selected = state.pieces.find((p) => p.id === s.selectedPieceId);
    const attack = s.cellTargets.some((t) => t.kind === 'attack');
    const power = s.cellTargets.some((t) => cellAt(ctx.board, t.x, t.y)?.powerPoint);
    const mage = selected ? ctx.roster[selected.character]?.spellcaster === true : false;
    const human = humanTurn(s);
    // Each test runs only while its lesson is still to come, so the threat search is never run
    // for a lesson already taught, nor ahead of the welcome.
    const candidates: [GuideStep, () => boolean][] = [
      // The opening lesson comes before the situational ones: the side moving first can leave a
      // piece under threat on the very first turn, and a player who has been told nothing yet
      // should read "tap one of your pieces" before "a red ring marks a piece the enemy could
      // take". A lesson replaced before it is read counts as taught, so out of order it is lost.
      ['welcome', () => human && !selected],
      ['aftermath', () => s.lastEvent?.type === 'battle-resolved' && human],
      ['cycle', () => turned && human],
      ['attack', () => human && !!selected && attack],
      ['power', () => human && !!selected && power],
      ['spells', () => human && mage],
      [
        'threats',
        () => human && !selected && threatsAgainst(ctx, state, state.activeFaction).length > 0,
      ],
      ['targets', () => human && !!selected],
    ];
    for (const [step, fires] of candidates) if (!this.seen.has(step) && fires()) return step;
    // Every lesson taught: one last word, then silence.
    if (human && GUIDE_STEPS.every((step) => step === 'done' || this.seen.has(step))) return 'done';
    return null;
  }
}
